import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { daysUntilBirthday } from "@/lib/birthday";

export interface BirthdayUser {
  id: string;
  full_name: string;
  avatar_url: string | null;
  birthday: string;
}

export interface UpcomingBirthday extends BirthdayUser {
  /** Days until next occurrence. 0 = today, 1–6 = this week */
  daysUntil: number;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

async function fetchActiveUsersWithBirthday(): Promise<BirthdayUser[]> {
  const { data, error } = await supabase
    .from("users")
    .select("id, full_name, avatar_url, birthday")
    .eq("is_active", true)
    .not("birthday", "is", null);
  if (error) throw error;
  return (data ?? []).filter((u): u is BirthdayUser => !!u.birthday);
}

export function useTodaysBirthdays() {
  return useQuery({
    queryKey: ["birthdays-today"],
    queryFn: async (): Promise<BirthdayUser[]> => {
      const users = await fetchActiveUsersWithBirthday();
      const now = new Date();
      const today = `${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
      return users.filter((u) => u.birthday.slice(5) === today);
    },
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
}

/**
 * Birthdays within the next 7 days (today + 6 days), sorted by proximity.
 */
export function useUpcomingBirthdays(windowDays: number = 7) {
  return useQuery({
    queryKey: ["birthdays-upcoming", windowDays],
    queryFn: async (): Promise<UpcomingBirthday[]> => {
      const users = await fetchActiveUsersWithBirthday();
      const now = new Date();
      return users
        .map((u) => ({ ...u, daysUntil: daysUntilBirthday(u.birthday, now) }))
        .filter((u) => u.daysUntil < windowDays)
        .sort((a, b) => a.daysUntil - b.daysUntil);
    },
    refetchInterval: 30 * 60_000,
    staleTime: 5 * 60_000,
  });
}
